import {
    Component,
    NgZone,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentLoader } from '../environments/utils/environment-loader.util';
import {
    CognitoUtil,
    LoggedInCallback,
} from './shared/services/cognito.service';
import { UserCreateService } from './shared/services/user-create.service';
import { UserProfileService } from './shared/services/user-profile.service';
import { UserSigninService } from './shared/services/user-signin.service';
import { CognitoAuth } from 'amazon-cognito-auth-js';
import { IGovService } from './shared/services/igov.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements LoggedInCallback {
    @ViewChild('popupTemplate') popupTemplate: TemplateRef<any>;
    private dialogRef: MatDialogRef<any>;

    public auth;
    public errorMessage: any;

    // some idle related fields to store our state so we can display it in the UI
    idleState = { visible: false, message: 'Not started.' };
    isDialogOpen = false;

    constructor(
        private router: Router,
        private userSignService: UserSigninService,
        private userProfileService: UserProfileService,
        private cognitoUtil: CognitoUtil,
        private userCreateService: UserCreateService,
        private igovService: IGovService,
        private idle: Idle,
        private dialog: MatDialog,
        private ngZone: NgZone,
        public translate: TranslateService,
    ) {
        //this.igovService.getIP();
        this.userSignService.isAuthenticated(this);
        // set idle parameters 840 + 60 = 900s = 15 min
        idle.setIdle(840); // how long can they be inactive before considered idle, in seconds if a user does not perform any interaction (such as clicking a button, moving the mouse, typing, etc.)
        idle.setTimeout(60); // how long can they be idle before considered timed out, in seconds
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // provide sources that will "interrupt" aka provide events indicating the user is active

        // do something when the user becomes idle
        idle.onIdleStart.subscribe(() => {
            this.idleState = {
                visible: false,
                message: "You've becomes idle!",
            };
        });
        // do something when the user is no longer idle
        idle.onIdleEnd.subscribe(() => {
            this.idleState = { visible: false, message: 'No longer idle.' };
            this.isDialogOpen = false;
            this.dialog.closeAll();
            this.ngZone.run(() => this.dialogRef.close());
        });
        // do something when the user has timed out
        idle.onTimeout.subscribe(() => {
            this.userSignService.logout();
            this.idle.stop();
            this.idleState = { visible: false, message: 'Timed out!' };
        });
        // do something as the timeout countdown does its thing
        idle.onTimeoutWarning.subscribe((seconds) => {
            const countdown = seconds;
            this.idleState = {visible: true,message: 'You will log out in <strong>' + countdown + '</strong> seconds!'};
            // Open the dialog
            if (this.idleState.visible && !this.isDialogOpen) {
                this.isDialogOpen = true;
                this.dialogRef = this.dialog.open(this.popupTemplate, {width: '400px'});
            }
        });
    }


    reset() {
        // we'll call this method when we want to start/reset the idle process
        // reset any component state and be sure to call idle.watch()
        this.idle.watch();
        this.idleState = { visible: false, message: 'Started.' };
    }

    ngOnInit(): void {
        let lang = localStorage.getItem('defaultLanguage') || 'en';
        this.translate.setDefaultLang(lang);
        const loginRequired = EnvironmentLoader.environment.loginRequired;
        if (loginRequired) {
            const currentUser =
                this.userSignService.cognitoUtil.getCurrentUser();
            if (!currentUser) this.router.navigate(['login']);
        }
        // right when the component initializes, start reset state and start watching
        if (this.userSignService.cognitoUtil.getCurrentUser() && loginRequired) {
            this.reset();
        } else {
            this.idle.stop();
        }
    }

    isLoggedIn(message: string, isLoggedIn: boolean) {
        if (isLoggedIn) {
            this.userProfileService.navigateAfterLogin();
        } else {
            //console.log("not logged in, initiate cognito");
            //console.log("isLogged is check called");
            this.auth = this.initCognito();
            var curUrl = window.location.href;
            this.auth.parseCognitoWebResponse(curUrl);
        }
    }

    initCognito() {
        let authData = {
            ClientId: EnvironmentLoader.environment.clientId, // Your client id here
            AppWebDomain: EnvironmentLoader.environment.appWebDomain,
            TokenScopesArray: [
                'profile',
                'email',
                'openid',
                'aws.cognito.signin.user.admin',
                'phone',
            ],
            RedirectUriSignIn: EnvironmentLoader.environment.callbackURL,
            RedirectUriSignOut: EnvironmentLoader.environment.signoutURL,
            IdentityProvider: EnvironmentLoader.environment.provider,
            UserPoolId: EnvironmentLoader.environment.userPoolId,
            AdvancedSecurityDataCollectionFlag: 'False',
        };

        let auth = new CognitoAuth(authData);
        auth.userhandler = {
            onSuccess: async (result) => {
                const cognitoUser = this.cognitoUtil.getCurrentUser();
                const currentUserName = cognitoUser['username'];
                await this.userSignService.setToken(cognitoUser);
                this.userCreateService
                    .userCreate(currentUserName)
                    .subscribe((res) => {
                        if (res['success']) {
                            // get current user's profile and assign current user's full name by the signed in user's username
                            this.userProfileService
                                .getProfile(currentUserName)
                                .subscribe((profileRes) => {
                                    localStorage.removeItem('user');
                                    localStorage.setItem(
                                        'user',
                                        JSON.stringify(profileRes['result'][0])
                                    );
                                    localStorage.removeItem('defaultLanguage');
                                    localStorage.setItem(
                                        'defaultLanguage',
                                        profileRes['result'][0][
                                            'USR_DefaultLanguage'
                                        ]
                                    );
                                    this.userProfileService.navigateAfterLogin();
                                });
                        } else {
                            this.userProfileService.navigateAfterLogin();
                        }
                    });
            },
            onFailure: (err) => {
                this.errorMessage = err;
            },
        };
        return auth;
    }
}
